import React from "react";
import FormGroup from "../components/fields/FormGroup";
import FormList from "../components/fields/FormList";
import Tuple from "../components/fields/Tuple";
import Registry from "../registry";
import t from "tcomb-form";
import utils from "./utils";
import sift from "sift";
import {
  stringArrayTransformer,
  objectTransformer,
  booleanTransformer,
} from "./fieldTransformers";

const mapValues = (obj, fn) =>
  Object.keys(obj).reduce((acc, key) => {
    acc[key] = fn(obj[key], key);
    return acc;
  }, {});

export const getContentSchema = (type, content) => {
  return type.parts.reduce((obj, partDefinition) => {
    obj[partDefinition.name] = getPartField(partDefinition, type, content);
    return obj;
  }, {});
};

export const getForm = (schema, value) => {
  const fieldOptions = {};
  const nextSchema = mapValues(filter(schema, value), (field, key) => {
    const structure = parseSchema(field);
    fieldOptions[key] = getFieldOptions(field);
    return structure;
  });

  return {
    schema: t.struct(nextSchema),
    options: {
      fields: fieldOptions,
    },
  };
};

export const getPartField = (partDefinition, type, content) => {
  const field = Registry.get(partDefinition.type) || Registry.get("text"); // fallback to text
  const fieldSchema = field.schema(partDefinition);
  fieldSchema.required = utils.isChecked(partDefinition.required);

  return {
    label: partDefinition.label,
    help: partDefinition.hint,
    config: {
      required: fieldSchema.required,
      size: partDefinition.size || 1,
      max: (type.options && type.options.display_columns) || 3,
      part: partDefinition,
      content,
    },
    ...fieldSchema,
    // ...{type, of, fields, allowedValues, label, help, template, component, factory, transformer}
  };
};

export const getPartDefinitionField = (partDefinition) => {
  const stringTemplate = () => <span />;
  const schema = {
    id: { type: "string", template: stringTemplate },
    display_order: { type: "number", template: stringTemplate },
    label: {
      type: "string",
      label: "Label",
      help: "How this field is display in the admin panel.",
      config: { required: true },
    },
    name: {
      type: "string",
      label: "Name",
      help: "How you access this field through the API.",
      config: { required: true },
    },
    hint: {
      type: "string",
      label: "Hint",
      help: "Displayed when entering content to help fill in this field.",
    },
    required: {
      type: "boolean",
      label: "Required?",
      required: false,
    },
    size: {
      type: "string",
      allowedValues: [1, 2, 3],
      label: "Display Size",
      nullOption: false,
    },
    type: {
      type: "string",
      allowedValues: Object.keys(Registry.types),
      options: Registry.list(),
      nullOption: false,
      label: "Type",
    },
    ...(Registry.get(partDefinition.type) || {}).options,
  };

  return mapValues(schema, (field) => {
    if (!field.config) field.config = {};
    field.config.type = "partDefinition";
    return field;
  });
};

export const filter = (schema, value) => {
  const newSchema = {};
  for (const key in schema) {
    const field = schema[key];
    if (field.condition) {
      const condition =
        typeof condition === "string"
          ? JSON.parse(field.condition)
          : field.condition;
      const visible = sift(condition)(value);
      if (!visible) continue;
    }
    newSchema[key] = field;
    if (field.type === "structure") {
      field.fields = filter(field.fields, value[key]);
    }
  }

  return newSchema;
};

export const getFieldOptions = (schema) => {
  switch (schema.type) {
    case "structure":
      schema.fields = mapValues(schema.fields, getFieldOptions);
      break;
    case "tuple":
      schema.factory = Tuple;
      schema.fields = schema.fields.map(getFieldOptions);
      break;
    case "list":
      if (!schema.factory) schema.factory = FormList;
      // backwards compat with older Fridge panel
      // allow lists to accept string delimited by \n
      if (!schema.transformer) schema.transformer = stringArrayTransformer;
      break;
    case "boolean":
      schema.transformer = booleanTransformer;
      break;
    case "dynamic":
      schema.factory = t.form.Textbox;
      schema.transformer = objectTransformer;
      break;
  }

  if (schema.component && !schema.template) {
    schema.template = (locals) => (
      <FormGroup locals={locals}>{schema.component(locals)}</FormGroup>
    );
  }

  return schema;
};

export const parseSchema = (schema) => {
  const parsers = {
    string: (schema) => {
      if (schema.allowedValues) {
        if (Array.isArray(schema.allowedValues)) {
          return t.enums.of(schema.allowedValues);
        } else {
          return t.enums(schema.allowedValues);
        }
      } else {
        return t.Str;
      }
    },
    number: (schema) => t.Num,
    boolean: (schema) => t.Bool,
    date: (schema) => t.Dat,
    list: (schema) => t.list(parseSchema(schema.of)),
    structure: (schema) => t.struct(mapValues(schema.fields, parseSchema)),
    tuple: (schema) => t.tuple(schema.fields.map(parseSchema)),
    dynamic: (schema) => t.Obj,
    object: (schema) => t.Obj,
  };

  const parserType =
    schema.type && parsers[schema.type] ? schema.type : "string";
  const type = parsers[parserType](schema);
  const required = schema.required || (schema.config && schema.config.required);
  return required ? type : t.maybe(type);
};

export default {
  getContentSchema,
  getForm,
  getPartField,
  getPartDefinitionField,
  getFieldOptions,
  parseSchema,
};



// WEBPACK FOOTER //
// ./app/lib/FormUtils.js